import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAgentBeta } from '../contexts/AgentBetaContext';
import { Building2, Bot, ChevronDown, User, LogOut, Settings, Shield, Sparkles, Bug, Megaphone, CreditCard, Globe } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../hooks/useAuth';
import { useSuperAdmin } from '../hooks/useSuperAdmin';
import { supabase } from '../lib/supabase';
import { clockOut, getTimeclockStatus } from '../lib/timeclock';
import LogoutWithActiveWorkModal, { type LogoutPromptVariant } from './LogoutWithActiveWorkModal';
import ReportBugModal from './ReportBugModal';
import PortalPanel from './PortalPanel';
import { useDismissOverlayProtectingCalendarCreate } from '../hooks/useDismissOverlayProtectingCalendarCreate';
// import ConnectAccountModal from './ConnectAccountModal';
// import { multiSessionManager, ActiveBusinessSession } from '../services/multiSessionManager';

interface BusinessProfileDropdownProps {
  className?: string;
}

export default function BusinessProfileDropdown({ className = '' }: BusinessProfileDropdownProps) {
  const { selectedBusiness, role, loading } = useBusiness();
  const agentsEnabled = selectedBusiness?.agents_enabled !== false;
  const { subscription, plans, fetchStripeDetails } = useSubscription();
  const { user } = useAuth();
  const { isSuperAdmin } = useSuperAdmin();
  const navigate = useNavigate();
  const { openAgents } = useAgentBeta();
  const [isOpen, setIsOpen] = useState(false);
  const [currentResource, setCurrentResource] = useState<any>(null);
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Logout with active work modal (timeclock and/or job timers)
  const [logoutPromptOpen, setLogoutPromptOpen] = useState(false);
  const [logoutPromptVariant, setLogoutPromptVariant] = useState<LogoutPromptVariant>('timeclock');
  const [logoutPromptTimerCount, setLogoutPromptTimerCount] = useState(0);
  const [logoutPromptLoading, setLogoutPromptLoading] = useState(false);
  const [openPayrollEntryForLogout, setOpenPayrollEntryForLogout] = useState<{ id: string; clock_in: string; break_start: string | null; break_end: string | null } | null>(null);
  const [activeJobTimerEntriesForLogout, setActiveJobTimerEntriesForLogout] = useState<{ booking_id: string }[]>([]);
  
  // Version state
  const [currentVersion, setCurrentVersion] = useState<string>('3.6');
  
  // Report a Bug modal
  const [isReportBugOpen, setIsReportBugOpen] = useState(false);
  
  // Portal panel
  const [isPortalOpen, setIsPortalOpen] = useState(false);
  const [portalUnreadCount, setPortalUnreadCount] = useState(0);

  const fetchPortalUnread = async () => {
    if (!selectedBusiness?.id) return;
    const { data: readData } = await supabase
      .from('portal_reads')
      .select('last_read_at')
      .eq('business_id', selectedBusiness.id)
      .maybeSingle();

    const lastRead = readData?.last_read_at || '1970-01-01T00:00:00Z';

    const { count } = await supabase
      .from('portal_updates')
      .select('*', { count: 'exact', head: true })
      .eq('business_id', selectedBusiness.id)
      .gt('created_at', lastRead);

    setPortalUnreadCount(count || 0);
  };

  useEffect(() => {
    fetchPortalUnread();
  }, [selectedBusiness?.id]);
  
  // Multi-session state - DISABLED FOR NOW
  // const [activeSessions, setActiveSessions] = useState<ActiveBusinessSession[]>([]);
  // const [currentSession, setCurrentSession] = useState<ActiveBusinessSession | null>(null);
  // const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);

  // Load active sessions - DISABLED FOR NOW
  // useEffect(() => {
  //   const loadSessions = () => {
  //     const sessions = multiSessionManager.getAllSessions();
  //     const current = multiSessionManager.getCurrentSession();
  //     setActiveSessions(sessions);
  //     setCurrentSession(current);
  //   };

  //   // Load on mount
  //   loadSessions();

  //   // Listen for storage changes (cross-tab sync)
  //   const handleStorageChange = () => {
  //     loadSessions();
  //   };
  //   window.addEventListener('multiSessionStorage', handleStorageChange);
  //   window.addEventListener('storage', handleStorageChange);

  //   return () => {
  //     window.removeEventListener('multiSessionStorage', handleStorageChange);
  //     window.removeEventListener('storage', handleStorageChange);
  //   };
  // }, []);

  // Fetch current resource info (only for non-admin users)
  useEffect(() => {
    const fetchCurrentResource = async () => {
      if (!selectedBusiness?.id || !user?.id || role === 'admin') {
        setCurrentResource(null);
        return;
      }

      try {
        const { data: resource, error } = await supabase
          .from('resources')
          .select('*')
          .eq('business_id', selectedBusiness.id)
          .eq('auth_user_id', user.id)
          .single();

        if (error) {
          console.error('Error fetching current resource:', error);
          return;
        }

        setCurrentResource(resource);
      } catch (error) {
        console.error('Error fetching current resource:', error);
      }
    };

    fetchCurrentResource();
  }, [selectedBusiness?.id, user?.id, role]);

  // Check if resource has admin access
  const resourceHasAdminAccess = currentResource?.is_admin === true;

  // Subscription menu item: plan name + trial countdown.
  const isTrialing = subscription?.subscription_status === 'trialing';
  const currentPlanName = (() => {
    const planId = subscription?.active_plan_id;
    if (!planId) return 'No plan';
    const plan = plans.find((p) => p.id === planId);
    if (plan?.name) return plan.name;
    return `${planId.charAt(0).toUpperCase()}${planId.slice(1)} Plan`;
  })();
  const trialDaysLeft = (() => {
    if (!isTrialing) return null;
    // For a trialing Stripe subscription the current period ends when the trial ends.
    const end = subscription?.stripeDetails?.currentPeriodEnd;
    if (!end) return null;
    const ms = new Date(end).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
  })();

  // Trial end date lives in Stripe — fetch it when the dropdown opens if missing.
  useEffect(() => {
    if (isOpen && isTrialing && !subscription?.stripeDetails) {
      void fetchStripeDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isTrialing]);

  // Load current version
  useEffect(() => {
    // Get current version from localStorage
    const storedVersion = localStorage.getItem('nexus-app-version') || '3.6';
    setCurrentVersion(storedVersion);

    // Update version when it changes in localStorage (from other tabs or updates)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'nexus-app-version' && e.newValue) {
        setCurrentVersion(e.newValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Update button position when opening dropdown
  const updateButtonPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setButtonRect(rect);
    }
  };

  // Close dropdown when clicking outside — do not also trigger calendar create under the cursor
  const closeDropdown = useCallback(() => setIsOpen(false), []);
  useDismissOverlayProtectingCalendarCreate(isOpen, closeDropdown, [dropdownRef, buttonRef]);

  // Multi-session switching - DISABLED FOR NOW
  // const handleSwitchBusiness = async (session: ActiveBusinessSession) => {
  //   try {
  //     await multiSessionManager.switchSession(session.sessionId);
  //     // Reload page to refresh all context
  //     window.location.reload();
  //   } catch (error) {
  //     console.error('Error switching business:', error);
  //     alert('Failed to switch business. Please try again.');
  //   }
  // };

  // const handleRemoveSession = async (sessionId: string, e: React.MouseEvent) => {
  //   e.stopPropagation();
  //   if (!confirm('Remove this account? You will need to log in again to access it.')) {
  //     return;
  //   }

  //   try {
  //     await multiSessionManager.removeSession(sessionId);
  //     // Reload sessions
  //     const sessions = multiSessionManager.getAllSessions();
  //     const current = multiSessionManager.getCurrentSession();
  //     setActiveSessions(sessions);
  //     setCurrentSession(current);
      
  //     // If we removed the current session, reload page
  //     if (currentSession?.sessionId === sessionId) {
  //       window.location.reload();
  //     }
  //   } catch (error) {
  //     console.error('Error removing session:', error);
  //     alert('Failed to remove account. Please try again.');
  //   }
  // };

  const performLogout = async () => {
    try {
      setIsOpen(false);
      const { signOut } = await import('../lib/auth');
      const { error } = await signOut();
      if (error) console.error('Error during logout:', error);
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      // Use window.location for a hard redirect to /login
      // This is more reliable than navigate() because the React tree
      // may be in a broken state after session destruction
      window.location.href = '/login';
    }
  };

  const handleStopAndLogout = async () => {
    if (!currentResource?.id || !selectedBusiness?.id || !supabase) return;
    setLogoutPromptLoading(true);
    try {
      if (openPayrollEntryForLogout) {
        // The engine closes the shift and stops any running job timers with it.
        await clockOut(selectedBusiness.id, { resourceId: currentResource.id, source: 'logout', stopJobTimers: true });
      } else {
        for (const { booking_id } of activeJobTimerEntriesForLogout) {
          await supabase.rpc('stop_job_timer', {
            p_booking_id: booking_id,
            p_resource_id: currentResource.id,
            p_method: 'manual',
          });
        }
      }
      setLogoutPromptOpen(false);
      setOpenPayrollEntryForLogout(null);
      setActiveJobTimerEntriesForLogout([]);
      await performLogout();
    } catch (error) {
      console.error('Error stopping work before logout:', error);
    } finally {
      setLogoutPromptLoading(false);
    }
  };

  const handleLogout = async () => {
    if (role === 'admin') {
      await performLogout();
      return;
    }
    if (!currentResource?.id || !selectedBusiness?.id) {
      await performLogout();
      return;
    }
    try {
      const status = await getTimeclockStatus(selectedBusiness.id, { resourceId: currentResource.id });
      const openEntry = status.entry;
      const activeTimers = status.active_job_timers.map((t) => ({ booking_id: t.booking_id }));
      const isClockedIn = !!openEntry?.id;
      const hasActiveTimers = activeTimers.length > 0;

      if (!isClockedIn && !hasActiveTimers) {
        await performLogout();
        return;
      }

      setIsOpen(false);
      if (isClockedIn) setOpenPayrollEntryForLogout(openEntry);
      if (hasActiveTimers) setActiveJobTimerEntriesForLogout(activeTimers);
      if (isClockedIn && hasActiveTimers) setLogoutPromptVariant('both');
      else if (hasActiveTimers) setLogoutPromptVariant('timers');
      else setLogoutPromptVariant('timeclock');
      setLogoutPromptTimerCount(activeTimers.length);
      setLogoutPromptOpen(true);
    } catch (error) {
      console.error('Error checking logout preconditions:', error);
      await performLogout();
    }
  };

  // Multi-session handler - DISABLED FOR NOW
  // const handleSessionAdded = (session: ActiveBusinessSession) => {
  //   // Reload sessions
  //   const sessions = multiSessionManager.getAllSessions();
  //   setActiveSessions(sessions);
  //   setIsOpen(false);
  // };

  if (!selectedBusiness) return null;

  // Check if user is a business owner (has owned_business_ids in metadata)
  const userIsBusinessOwner = user?.user_metadata?.owned_business_ids;
  const isAdmin = userIsBusinessOwner || role === 'admin';
  

  return (
    <div className={`relative ${className}`}>
      {/* Profile Icon Button */}
      <button
        ref={buttonRef}
        onClick={() => {
          if (!isOpen) {
            updateButtonPosition();
          }
          setIsOpen(!isOpen);
        }}
        className="flex items-center justify-center w-8 h-8 rounded-full overflow-hidden hover:ring-2 hover:ring-white/20 transition-all duration-200"
        title={isAdmin ? selectedBusiness.name : `${currentResource?.first_name} ${currentResource?.last_name}`}
        data-assistant-anchor="profile-icon"
      >
        {isAdmin ? (
          // Business Logo or Building Icon
          selectedBusiness.booking_settings?.logo_url ? (
            <img 
              src={selectedBusiness.booking_settings.logo_url} 
              alt={`${selectedBusiness.name} logo`}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex items-center justify-center w-8 h-8 bg-gray-600 rounded-full">
              <Building2 className="h-4 w-4 text-white" />
            </div>
          )
        ) : (
          // Resource Profile Photo or Initials
          currentResource?.avatar_url ? (
            <img 
              src={currentResource.avatar_url} 
              alt={`${currentResource.first_name} ${currentResource.last_name}`}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex items-center justify-center w-8 h-8 bg-gray-600 rounded-full text-xs font-medium text-white">
              {currentResource?.first_name?.[0]}{currentResource?.last_name?.[0]}
            </div>
          )
        )}
      </button>

      {/* Dropdown Menu - Rendered via Portal */}
      {isOpen && buttonRect && createPortal(
        <div 
          ref={dropdownRef}
          className="fixed w-64 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-[9999]"
          style={{
            top: buttonRect.bottom + 8,
            right: window.innerWidth - buttonRect.right,
          }}
        >
          {/* Profile Header */}
          {isAdmin ? (
            <button
              onClick={() => {
                navigate('/business-settings');
                setIsOpen(false);
              }}
              className="w-full px-4 py-3 border-b border-gray-700 hover:bg-gray-700 transition-colors duration-200 text-left"
            >
              <div className="flex items-center space-x-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate">
                    {selectedBusiness.name}
                  </div>
                  {selectedBusiness.address && (
                    <div className="text-xs text-gray-400 truncate">
                      {selectedBusiness.address}
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 text-xs text-blue-400 mt-1">
                    <Settings className="h-3.5 w-3.5" />
                    Business Settings
                  </div>
                </div>
              </div>
            </button>
          ) : (
            <button
              onClick={() => {
                navigate('/profile');
                setIsOpen(false);
              }}
              className="w-full px-4 py-3 border-b border-gray-700 hover:bg-gray-700 transition-colors duration-200 text-left"
            >
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-full overflow-hidden">
                  {currentResource?.avatar_url ? (
                    <img 
                      src={currentResource.avatar_url} 
                      alt={`${currentResource.first_name} ${currentResource.last_name}`}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center w-10 h-10 bg-gray-600 rounded-full text-sm font-medium text-white">
                      {currentResource?.first_name?.[0]}{currentResource?.last_name?.[0]}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-white truncate">
                    {`${currentResource?.first_name} ${currentResource?.last_name}`}
                  </div>
                  <div className="text-xs text-gray-400 truncate">
                    {currentResource?.roleName || currentResource?.role || 'Team Member'}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-blue-400 mt-1">
                    <User className="h-3.5 w-3.5" />
                    Profile
                  </div>
                </div>
              </div>
            </button>
          )}

          {/* Menu Items */}
          <div className="py-1">
            {!isAdmin && (
              <>
              <button
                onClick={() => {
                  navigate('/profile');
                  setIsOpen(false);
                }}
                className="w-full flex items-center px-4 py-2 text-sm text-white hover:bg-gray-700 transition-colors duration-200"
              >
                <User className="h-4 w-4 mr-3" />
                Profile
              </button>
                
                {/* Business Settings option for resources with admin access */}
                {resourceHasAdminAccess && (
                  <>
                    <div className="my-1 border-t border-gray-700"></div>
                    {/* Subscription — same as the admin item; admin-granted resources manage billing too */}
                    <button
                      onClick={() => {
                        navigate('/my-apps');
                        setIsOpen(false);
                      }}
                      className="w-full flex items-start px-4 py-2 text-sm text-white hover:bg-gray-700 transition-colors duration-200 text-left"
                    >
                      <CreditCard className="h-4 w-4 mr-3 mt-0.5 shrink-0" />
                      <span className="flex-1 min-w-0">
                        <span className="block">Subscription</span>
                        {isTrialing ? (
                          <span className="block text-xs text-amber-400 truncate">
                            Trial{trialDaysLeft != null ? ` · ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left` : ''}
                          </span>
                        ) : (
                          <span className="block text-xs text-gray-400 truncate">{currentPlanName}</span>
                        )}
                      </span>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/business-settings');
                        setIsOpen(false);
                      }}
                      className="w-full flex items-center px-4 py-2 text-sm text-white hover:bg-gray-700 transition-colors duration-200"
                    >
                      <Settings className="h-4 w-4 mr-3" />
                      Business Settings
                    </button>
                    {agentsEnabled && (
                      <button
                        onClick={() => {
                          openAgents();
                          setIsOpen(false);
                        }}
                        className="w-full flex items-center px-4 py-2 text-sm text-white hover:bg-gray-700 transition-colors duration-200"
                      >
                        <Bot className="h-4 w-4 mr-3" />
                        Agents
                      </button>
                    )}
                    <button
                      onClick={() => {
                        navigate('/ai-settings');
                        setIsOpen(false);
                      }}
                      className="w-full flex items-center px-4 py-2 text-sm text-white hover:bg-gray-700 transition-colors duration-200"
                    >
                      <Sparkles className="h-4 w-4 mr-3" />
                      AI Settings
                    </button>
                  </>
                )}
              </>
            )}
            
            {isAdmin && (
              <>
                {/* Subscription — plan / trial status, routes to My Apps (Nexus Connect) */}
                <button
                  onClick={() => {
                    navigate('/my-apps');
                    setIsOpen(false);
                  }}
                  className="w-full flex items-start px-4 py-2 text-sm text-white hover:bg-gray-700 transition-colors duration-200 text-left"
                >
                  <CreditCard className="h-4 w-4 mr-3 mt-0.5 shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block">Subscription</span>
                    {isTrialing ? (
                      <span className="block text-xs text-amber-400 truncate">
                        Trial{trialDaysLeft != null ? ` · ${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left` : ''}
                      </span>
                    ) : (
                      <span className="block text-xs text-gray-400 truncate">{currentPlanName}</span>
                    )}
                  </span>
                </button>

                {/* Website — connected site and hosting status */}
                <button
                  onClick={() => {
                    navigate('/websites');
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center px-4 py-2 text-sm text-white hover:bg-gray-700 transition-colors duration-200"
                >
                  <Globe className="h-4 w-4 mr-3" />
                  Website
                </button>

                {/* Super Admin Link - only show for super admins */}
                {isSuperAdmin && (
                  <>
                    <div className="my-2 border-t border-gray-700"></div>
                    <button
                      onClick={() => {
                        navigate('/super-admin');
                        setIsOpen(false);
                      }}
                      className="w-full flex items-center px-4 py-2 text-sm text-white hover:bg-gray-700 transition-colors duration-200"
                    >
                      <Shield className="h-4 w-4 mr-3" />
                      Super Admin
                    </button>
                  </>
                )}

                {/* Agents + AI Settings - only for admins */}
                {agentsEnabled && (
                  <button
                    onClick={() => {
                      openAgents();
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center px-4 py-2 text-sm text-white hover:bg-gray-700 transition-colors duration-200"
                  >
                    <Bot className="h-4 w-4 mr-3" />
                    Agents
                  </button>
                )}
                <button
                  onClick={() => {
                    navigate('/ai-settings');
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center px-4 py-2 text-sm text-white hover:bg-gray-700 transition-colors duration-200"
                >
                  <Sparkles className="h-4 w-4 mr-3" />
                  AI Settings
                </button>
                
                {/* Switch Business Section - DISABLED FOR NOW */}
                {/* {activeSessions.length > 1 && (
                  <>
                    <div className="my-2 border-t border-gray-700"></div>
                    <div className="px-4 py-2">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                        Switch Business
                      </p>
                    </div>
                    {activeSessions.map((session) => {
                      const isCurrentSession = currentSession?.sessionId === session.sessionId;
                      return (
                        <button
                          key={session.sessionId}
                          onClick={() => !isCurrentSession && handleSwitchBusiness(session)}
                          className={`w-full flex items-center justify-between px-4 py-2 text-sm transition-colors duration-200 ${
                            isCurrentSession
                              ? 'bg-gray-700 text-white cursor-default'
                              : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                          }`}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {session.businessLogo ? (
                              <img
                                src={session.businessLogo}
                                alt={session.businessName}
                                className="w-6 h-6 rounded object-cover flex-shrink-0"
                              />
                            ) : (
                              <div className="w-6 h-6 bg-gray-600 rounded flex items-center justify-center flex-shrink-0">
                                <Building2 className="h-3 w-3 text-white" />
                              </div>
                            )}
                            <span className="truncate">{session.businessName}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {isCurrentSession && (
                              <Check className="h-4 w-4 text-blue-400 flex-shrink-0" />
                            )}
                            {!isCurrentSession && (
                              <button
                                onClick={(e) => handleRemoveSession(session.sessionId, e)}
                                className="p-1 hover:bg-gray-600 rounded transition-colors"
                                title="Remove account"
                              >
                                <X className="h-3 w-3 text-gray-400 hover:text-red-400" />
                              </button>
                            )}
                          </div>
                        </button>
                      );
                    })}
                    <div className="my-2 border-t border-gray-700"></div>
                  </>
                )} */}
                
                {/* Connect Account Button - DISABLED FOR NOW */}
                {/* <button
                  onClick={() => {
                    setIsConnectModalOpen(true);
                    setIsOpen(false);
                  }}
                  className="w-full flex items-center px-4 py-2 text-sm text-white hover:bg-gray-700 transition-colors duration-200"
                >
                  <LinkIcon className="h-4 w-4 mr-3" />
                  {activeSessions.length > 0 ? 'Connect Another Account' : 'Connect Account'}
                </button> */}
              </>
            )}
            
            <div className="my-1 border-t border-gray-700"></div>
            {isAdmin && (
              <button
                onClick={() => {
                  setIsPortalOpen(true);
                  setIsOpen(false);
                }}
                className="w-full flex items-center px-4 py-2 text-sm text-white hover:bg-gray-700 transition-colors duration-200"
              >
                <Megaphone className="h-4 w-4 mr-3" />
                Portal
                {portalUnreadCount > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                    {portalUnreadCount > 9 ? '9+' : portalUnreadCount}
                  </span>
                )}
              </button>
            )}
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-4 py-2 text-sm text-white hover:bg-gray-700 transition-colors duration-200"
            >
              <LogOut className="h-4 w-4 mr-3" />
              Log out
            </button>
            
            {/* Version Display */}
            <div className="border-t border-gray-700 mt-1">
              <div className="px-4 py-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">Version</span>
                    <span className="text-xs font-medium text-gray-300">{currentVersion}</span>
                  </div>
                  <span className="text-xs text-gray-500">Auto-updates enabled</span>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Log out with active timeclock / job timers */}
      <LogoutWithActiveWorkModal
        isOpen={logoutPromptOpen}
        onClose={() => {
          if (!logoutPromptLoading) {
            setLogoutPromptOpen(false);
            setOpenPayrollEntryForLogout(null);
            setActiveJobTimerEntriesForLogout([]);
          }
        }}
        variant={logoutPromptVariant}
        activeTimerCount={logoutPromptTimerCount}
        onStopAndLogout={handleStopAndLogout}
        loading={logoutPromptLoading}
      />

      {/* Report a Bug Modal */}
      <ReportBugModal
        isOpen={isReportBugOpen}
        onClose={() => setIsReportBugOpen(false)}
      />

      {/* Portal Panel */}
      <PortalPanel
        isOpen={isPortalOpen}
        onClose={() => setIsPortalOpen(false)}
        businessId={selectedBusiness?.id || ''}
        businessName={selectedBusiness?.name || ''}
        isSuperAdmin={isSuperAdmin}
        onRead={() => setPortalUnreadCount(0)}
      />

      {/* Connect Account Modal - DISABLED FOR NOW */}
      {/* <ConnectAccountModal
        isOpen={isConnectModalOpen}
        onClose={() => setIsConnectModalOpen(false)}
        onSessionAdded={handleSessionAdded}
      /> */}
    </div>
  );
}
